/**
 * 🛡️ ROOM MEMBERSHIP ENFORCEMENT SYSTEM
 * 
 * Ensures walls never lose their room associations during operations like:
 * - Wall splits
 * - Room resizes 
 * - Extrusion creation
 * - Wall movements
 * 
 * This prevents the critical bug where walls disappear or rooms become disconnected.
 */

// Import proper types from the main types file
type WallType = 'HLP' | 'PW' | 'SW' | 'None';

type Wall = {
  start: { x: number; y: number };
  end: { x: number; y: number };
  floor: number; // Required by the Wall interface
  type: WallType; // Required by the Wall interface - exact union type
  [key: string]: any; // Allow additional properties like tempId, originatingRoomId, etc.
};

type Room = {
  id: string;
  name: string;
  stableId?: string; // Make optional to match actual Room interface
  center: { x: number; y: number };
  walls: Wall[];
  area: number;
};

interface WallRoomMembership {
  wallId: string;
  originatingRoomId?: string;
  assignedRoomIds: string[];
  parentRoomIds?: string[];
}

/**
 * 🔍 AUDIT: Validate that all walls have proper room membership
 */
export function auditWallRoomMembership(walls: Wall[], rooms: Room[]): {
  valid: boolean;
  orphanedWalls: Wall[];
  issues: string[];
} {
  const orphanedWalls: Wall[] = [];
  const issues: string[] = [];

  walls.forEach(wall => {
    const hasRoomOwnership = !!(wall as any).originatingRoomId || 
                           !!(wall as any).assignedRoomIds?.length ||
                           !!(wall as any).parentRoomIds?.length;

    if (!hasRoomOwnership) {
      orphanedWalls.push(wall);
      issues.push(`Wall ${(wall as any).tempId || 'unknown'} at (${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y}) has no room membership`);
    }

    // Check if assigned rooms actually exist (with tolerance for different ID formats)
    if ((wall as any).assignedRoomIds?.length) {
      (wall as any).assignedRoomIds.forEach((roomId: string) => {
        const roomExists = rooms.some(room => 
          room.id === roomId || 
          room.stableId === roomId ||
          room.name === roomId ||
          // Tolerance for room ID variations (room_1 vs Room 1 etc.)
          room.stableId?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === roomId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
        );
        if (!roomExists) {
          // Only flag as issue if wall has no other valid room membership
          const hasOtherMembership = !!(wall as any).originatingRoomId || !!(wall as any).parentRoomIds?.length;
          if (!hasOtherMembership) {
            issues.push(`Wall ${(wall as any).tempId || 'unknown'} is assigned to non-existent room ${roomId}`);
          }
        }
      });
    }
    
    // Similar tolerance check for originatingRoomId and parentRoomIds
    if ((wall as any).originatingRoomId) {
      const roomId = (wall as any).originatingRoomId;
      const roomExists = rooms.some(room => 
        room.id === roomId || 
        room.stableId === roomId ||
        room.name === roomId ||
        room.stableId?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === roomId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
      );
      if (!roomExists) {
        // Only flag as issue if this is the only room membership and no assigned/parent rooms exist
        const hasOtherMembership = !!(wall as any).assignedRoomIds?.length || !!(wall as any).parentRoomIds?.length;
        if (!hasOtherMembership) {
          issues.push(`Wall ${(wall as any).tempId || 'unknown'} originates from non-existent room ${roomId}`);
        }
      }
    }
  });

  return {
    valid: orphanedWalls.length === 0 && issues.length === 0,
    orphanedWalls,
    issues
  };
}

/**
 * 🔧 REPAIR: Automatically fix orphaned walls by assigning them to nearby rooms
 */
export function repairOrphanedWalls(orphanedWalls: Wall[], rooms: Room[]): Wall[] {
  const repairedWalls: Wall[] = [];

  orphanedWalls.forEach(wall => {
    console.log('🛡️ REPAIRING_ORPHANED_WALL', {
      wallId: (wall as any).tempId,
      wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`,
      availableRooms: rooms.length
    });

    // Find the room this wall most likely belongs to by checking spatial containment
    let bestRoom: Room | null = null;
    let bestDistance = Infinity;

    rooms.forEach((room: Room) => {
      // Calculate distance from wall midpoint to room center
      const wallMidX = (wall.start.x + wall.end.x) / 2;
      const wallMidY = (wall.start.y + wall.end.y) / 2;
      const distance = Math.sqrt(
        Math.pow(wallMidX - room.center.x, 2) + 
        Math.pow(wallMidY - room.center.y, 2)
      );

      // Also check if any of the room's walls are connected to this orphaned wall
      const isConnected = room.walls.some((roomWall: Wall) => {
        const tolerance = 5; // pixels
        return (
          (Math.abs(roomWall.start.x - wall.start.x) <= tolerance && Math.abs(roomWall.start.y - wall.start.y) <= tolerance) ||
          (Math.abs(roomWall.start.x - wall.end.x) <= tolerance && Math.abs(roomWall.start.y - wall.end.y) <= tolerance) ||
          (Math.abs(roomWall.end.x - wall.start.x) <= tolerance && Math.abs(roomWall.end.y - wall.start.y) <= tolerance) ||
          (Math.abs(roomWall.end.x - wall.end.x) <= tolerance && Math.abs(roomWall.end.y - wall.end.y) <= tolerance)
        );
      });

      if (isConnected || distance < bestDistance) {
        bestRoom = room as Room; // Explicit type assertion
        bestDistance = distance;
      }
    });

    if (bestRoom !== null) {
      const selectedRoom = bestRoom as Room;
      const repairedWall: Wall = {
        ...wall,
        type: (wall.type as WallType) || 'None', // Ensure type property is valid WallType
        floor: wall.floor || 0, // Ensure floor property is present
        originatingRoomId: selectedRoom.id,
        assignedRoomIds: [selectedRoom.id],
        parentRoomIds: [selectedRoom.id]
      };

      repairedWalls.push(repairedWall);

      console.log('🛡️ ORPHANED_WALL_REPAIRED', {
        wallId: (wall as any).tempId,
        wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`,
        assignedToRoom: selectedRoom.name,
        assignedToRoomId: selectedRoom.id,
        repairMethod: bestDistance === 0 ? 'connected' : 'proximity'
      });
    } else {
      console.warn('🚨 ORPHANED_WALL_REPAIR_FAILED', {
        wallId: (wall as any).tempId,
        wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`,
        reason: 'No suitable room found'
      });
      // Keep the wall as-is but log the failure
      repairedWalls.push(wall);
    }
  });

  return repairedWalls;
}

/**
 * 🛡️ ENFORCE: Main function to audit and repair wall-room membership
 */
export function enforceWallRoomMembership(walls: Wall[], rooms: Room[]): {
  walls: Wall[];
  repairsApplied: number;
  issues: string[];
} {


  // Step 1: Audit current state
  const audit = auditWallRoomMembership(walls, rooms);
  
  if (audit.valid) {

    return {
      walls,
      repairsApplied: 0,
      issues: []
    };
  }

  console.warn('🚨 WALL_ROOM_MEMBERSHIP_ISSUES_FOUND', {
    orphanedWallCount: audit.orphanedWalls.length,
    issueCount: audit.issues.length,
    issues: audit.issues
  });

  // Step 2: Repair orphaned walls
  const repairedWalls = repairOrphanedWalls(audit.orphanedWalls, rooms);
  
  // Step 3: Merge repaired walls back with valid walls
  const validWalls = walls.filter(wall => 
    !audit.orphanedWalls.some(orphan => 
      wall.start.x === orphan.start.x && 
      wall.start.y === orphan.start.y &&
      wall.end.x === orphan.end.x && 
      wall.end.y === orphan.end.y
    )
  );

  const finalWalls = [...validWalls, ...repairedWalls];

  console.log('🛡️ WALL_ROOM_MEMBERSHIP_ENFORCEMENT_COMPLETE', {
    originalWallCount: walls.length,
    finalWallCount: finalWalls.length,
    repairsApplied: repairedWalls.length,
    remainingIssues: audit.issues.length
  });

  return {
    walls: finalWalls,
    repairsApplied: repairedWalls.length,
    issues: audit.issues
  };
}

/**
 * 🔧 PRESERVE RESIZE: Enhanced wall metadata preservation specifically for resize operations
 */
export function preserveWallMetadataForResize(newWall: Wall, originalWall: Wall): Wall {
  console.log('🔧 PRESERVING_WALL_METADATA_FOR_RESIZE', {
    newWallId: (newWall as any).tempId,
    originalWallId: (originalWall as any).tempId,
    originalHasOwnership: !!((originalWall as any).originatingRoomId || (originalWall as any).assignedRoomIds),
    coords: `(${newWall.start.x},${newWall.start.y})->(${newWall.end.x},${newWall.end.y})`
  });

  // Comprehensive metadata copying from original to new wall
  const preservedWall = {
    ...newWall,
    // Preserve room membership metadata
    ...(((originalWall as any).originatingRoomId) && { originatingRoomId: (originalWall as any).originatingRoomId }),
    ...(((originalWall as any).assignedRoomIds) && { assignedRoomIds: [...(originalWall as any).assignedRoomIds] }),
    ...(((originalWall as any).parentRoomIds) && { parentRoomIds: [...(originalWall as any).parentRoomIds] }),
    
    // Preserve split wall metadata
    ...(((originalWall as any).isSplitOrigin) && { isSplitOrigin: (originalWall as any).isSplitOrigin }),
    ...(((originalWall as any).parentWallId) && { parentWallId: (originalWall as any).parentWallId }),
    ...(((originalWall as any).splitFromWallId) && { splitFromWallId: (originalWall as any).splitFromWallId }),
    
    // Preserve extrusion metadata
    ...(((originalWall as any).isMainExtrusionSegment) && { isMainExtrusionSegment: (originalWall as any).isMainExtrusionSegment }),
    ...(((originalWall as any).isExtrusionConnector) && { isExtrusionConnector: (originalWall as any).isExtrusionConnector }),
    ...(((originalWall as any).requiresExtrusionOnMove) && { requiresExtrusionOnMove: (originalWall as any).requiresExtrusionOnMove }),
    
    // Preserve other important metadata
    ...(((originalWall as any).customLabel) && { customLabel: (originalWall as any).customLabel }),
    ...(((originalWall as any).wallHeight) && { wallHeight: (originalWall as any).wallHeight }),
    ...(((originalWall as any).thickness) && { thickness: (originalWall as any).thickness })
  };

  console.log('✅ WALL_METADATA_PRESERVED_FOR_RESIZE', {
    wallId: (preservedWall as any).tempId,
    preservedOwnership: !!((preservedWall as any).originatingRoomId || (preservedWall as any).assignedRoomIds),
    metadataFields: Object.keys(preservedWall).filter(key => !['start', 'end', 'type', 'floor'].includes(key))
  });

  return preservedWall;
}

/**
 * 🔄 BATCH PRESERVE: Preserve metadata for multiple walls during resize operations
 */
export function batchPreserveWallMetadataForResize(newWalls: Wall[], originalWalls: Wall[]): Wall[] {
  console.log('🔄 BATCH_PRESERVING_WALL_METADATA_FOR_RESIZE', {
    newWallCount: newWalls.length,
    originalWallCount: originalWalls.length
  });

  return newWalls.map((newWall, index) => {
    // Try to find corresponding original wall by tempId first
    let originalWall = originalWalls.find(orig => 
      (orig as any).tempId && (newWall as any).tempId && 
      (orig as any).tempId === (newWall as any).tempId
    );

    // If no tempId match, try to match by index (fallback for resize operations)
    if (!originalWall && originalWalls[index]) {
      originalWall = originalWalls[index];
    }

    // If still no match, try to find by coordinate proximity (last resort)
    if (!originalWall) {
      const tolerance = 10; // pixels
      originalWall = originalWalls.find(orig => {
        const startDistance = Math.hypot(
          orig.start.x - newWall.start.x,
          orig.start.y - newWall.start.y
        );
        const endDistance = Math.hypot(
          orig.end.x - newWall.end.x,
          orig.end.y - newWall.end.y
        );
        return startDistance <= tolerance && endDistance <= tolerance;
      });
    }

    if (originalWall) {
      return preserveWallMetadataForResize(newWall, originalWall);
    } else {
      console.warn('⚠️ NO_ORIGINAL_WALL_FOUND_FOR_PRESERVATION', {
        newWallIndex: index,
        newWallTempId: (newWall as any).tempId,
        coords: `(${newWall.start.x},${newWall.start.y})->(${newWall.end.x},${newWall.end.y})`
      });
      return newWall;
    }
  });
}

/**
 * 🔄 PROPAGATE: Ensure split walls and extrusion connectors inherit parent room membership
 */
export function propagateRoomMembership(wall: Wall, parentWall?: Wall, parentRoom?: Room): Wall {
  let roomMembership = {
    originatingRoomId: (wall as any).originatingRoomId,
    assignedRoomIds: (wall as any).assignedRoomIds ? [...(wall as any).assignedRoomIds] : undefined,
    parentRoomIds: (wall as any).parentRoomIds ? [...(wall as any).parentRoomIds] : undefined
  };

  // If wall lacks room membership, inherit from parent wall
  if (!roomMembership.originatingRoomId && !roomMembership.assignedRoomIds && parentWall) {
    roomMembership.originatingRoomId = (parentWall as any).originatingRoomId;
    roomMembership.assignedRoomIds = (parentWall as any).assignedRoomIds ? [...(parentWall as any).assignedRoomIds] : undefined;
    roomMembership.parentRoomIds = (parentWall as any).parentRoomIds ? [...(parentWall as any).parentRoomIds] : undefined;

    console.log('🔄 ROOM_MEMBERSHIP_PROPAGATED_FROM_PARENT_WALL', {
      wallId: (wall as any).tempId,
      parentWallId: (parentWall as any).tempId,
      inheritedRoomId: roomMembership.originatingRoomId
    });
  }

  // If still no membership and we have a parent room, use that
  if (!roomMembership.originatingRoomId && !roomMembership.assignedRoomIds && parentRoom) {
    roomMembership.originatingRoomId = parentRoom.id;
    roomMembership.assignedRoomIds = [parentRoom.id];
    roomMembership.parentRoomIds = [parentRoom.id];

    console.log('🔄 ROOM_MEMBERSHIP_PROPAGATED_FROM_PARENT_ROOM', {
      wallId: (wall as any).tempId,
      parentRoomId: parentRoom.id,
      parentRoomName: parentRoom.name
    });
  }

  return {
    ...wall,
    ...(roomMembership.originatingRoomId && { originatingRoomId: roomMembership.originatingRoomId }),
    ...(roomMembership.assignedRoomIds && { assignedRoomIds: roomMembership.assignedRoomIds }),
    ...(roomMembership.parentRoomIds && { parentRoomIds: roomMembership.parentRoomIds })
  };
}
